/**
 * Composite header for the single-order layout.
 *
 * Composes patient identification, status badges, authorization details,
 * action buttons, and header modals into a single row.
 * @module header
 */
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { BadgeWithIconProps } from "../../../../data-model/badgeWithIconProps";
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../../data-model/medicalPaOrdersModel";
import { NarRpsStatus } from "../../../../enums/evBvWriteBackStatus";
import { Submission } from "../../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { fetchNccnGuidelinesDocuments } from "../../../../redux/slice/medicalPa/guidelinesDocumentsSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { containsHealthFirst } from "../../../../utils/containsHealthFirst";

import { usePrimaryIcdCodes } from "../../../../utils/getPrimaryDiagnosisCodes";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import {
  handleEditIcd,
  handleEditSiteNpi,
} from "../../actionHandlers/authorizationDetailsActionHandler";
import { useAuthOnFileRefresh } from "../../hooks/useAuthOnFileRefresh";
import { useHeaderBadges } from "../../hooks/useHeaderBadges";
import { useHeaderModals } from "../../hooks/useHeaderModals";
import { formatJcodeIcdFromAuthOnFile } from "../../utils/getJCode";
import { logPrimaryIcdEditButtonClicked } from "../../utils/headerEventLogger";
import AuthorizationDetails from "./components/authorizationDetails";
import HeaderActionButtons from "./components/headerActionButtons";
import HeaderModals from "./components/headerModals";
import HeaderStatusBadges from "./components/headerStatusBadges";

/**
 * Props for {@link HeaderSingleOrder}.
 */
interface AuthHeaderProps {
  authorizationId?: string;
  patientName?: string;
  patientMrn?: string;
  location?: string;
  status?: string;
  orderId: string;
  dateOfService?: string;
  providerName?: string;
  insuranceName?: string;
  hideButton?: boolean;
  orderData?: GeneralOrderDetails;
  authOnFileDetails?: AuthOnFile;
  coverageDetails?: SingleCoverage[];
  /** Submission data for ICD codes, regimen state, and denial-risk checks. */
  submissionData?: Submission;
  badgeList?: BadgeWithIconProps[];
  selectedBadge?: BadgeWithIconProps;
  setSelectedBadge?: (badgeId: string) => void;
  setShowCigNoteModal?: (show: boolean) => void;
  /** Callback to re-fetch all data domains from the accumulator context. */
  refetchAllData: () => void;
}

/**
 * Single-order layout header: patient row, badges, authorization details, actions, and modals.
 *
 * @param props - Patient, order, coverage, and badge state for the header
 * @returns Full-width header for the authorization layout
 */
const HeaderSingleOrder = (props: AuthHeaderProps) => {
  const jcode_icd = useMemo(
    () => formatJcodeIcdFromAuthOnFile(props.authOnFileDetails ?? undefined),
    [props.authOnFileDetails],
  );
  const selectedProvider = useRef<{ id: string; name: string }>({
    id: "",
    name: "",
  });

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const {
    data: healthCareFacilityData,
    loading: isHealthCareFacilityLoading,
    error: healthCareFacilityError,
  } = useSelector((state: RootState) => state.singleHealthFacilityDetails);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { getStatusBadge, allDenialRiskLabels } = useHeaderBadges({
    authOnFile: props.authOnFileDetails,
    status: props.status,
    authStatusOptions,
    submissionData: props.submissionData,
  });

  const { manualAuthStatus } = useBusniessOfficeNotesContext();
  const { fetchAuthOnFileDetails } = useDataAccumulatorForSingleOrder();

  const { openedModalId } = useSelector((state: RootState) => state.modal);
  //TODO : get this data from the pgsql endpoint via custom hook
  const { data: guidelinesDocuments } = useSelector(
    (state: RootState) => state.guidelinesDocuments,
  );
  const dispatch = useDispatch<AppDispatch>();

  const { handleAuthOnFileRefresh, isRefreshing } = useAuthOnFileRefresh({
    orderId: props?.orderId ?? "",
    patientMrn: props?.orderData?.payload?.patient_id,
    drugName: props?.orderData?.payload?.regimen_name,
    generalOrderDetails: props?.orderData,
    onSuccess: () => {
      const orgId = getOrgIdForFetchExternalWorklist();
      fetchAuthOnFileDetails(props?.orderId ?? "", orgId);
    },
  });

  const {
    handleHappyFlowModalSave,
    handleNarRpaTriggerClick,
    handleCommentRpaModalSave,
    handleCloseModal,
    isLoading,
  } = useHeaderModals({
    generalOrderDetails: props.orderData,
    authOnFile: props.authOnFileDetails,
    coverageDetails: props.coverageDetails,
    submissionData: props.submissionData,
    selectedProvider,
  });

  useEffect(() => {
    if (props.orderId) {
      dispatch(fetchNccnGuidelinesDocuments({ order_id: props.orderId }));
    }
  }, [props.orderId, dispatch]);

  const shouldShowThreeDots = useMemo(() => {
    if (props.authOnFileDetails) {
      return true;
    }
    return false;
  }, [props.authOnFileDetails]);

  const shouldShowNarRpaTrigger = useMemo(() => {
    return containsHealthFirst(props?.insuranceName ?? "");
  }, [props?.insuranceName]);

  const isNarRpaInProgress = useMemo(() => {
    return (
      props.orderData?.status?.nar_rpa_status === NarRpsStatus.IN_PROGRESS ||
      props.orderData?.status?.nar_rpa_status === NarRpsStatus.QUEUED
    );
  }, [props.orderData?.status?.nar_rpa_status]);

  //TODO : Need to figure out the mapping for this in postgres
  const primaryIcdCodes = usePrimaryIcdCodes(props.submissionData ?? undefined);

  const primaryPayerName = useMemo(
    () =>
      props.coverageDetails?.find(
        (coverage) => coverage.coverage_type === "primary",
      )?.payer_name ?? "",
    [props.coverageDetails],
  );

  return (
    <div className="authorization-layout--headerflex flex h-full w-full flex-row items-center justify-between border-b-2 border-dashed border-primaryGray-15 pb-3 pt-0">
      <div className="flex flex-col items-start gap-2">
        <div className="authorization-layout--header__left flex h-full flex-row items-center gap-2 px-1">
          <div className="authorization-layout--header__left--name flex flex-row items-center gap-1">
            <div className="text-h16 font-bold">{props.patientName}</div>
            <div>{`(${props.patientMrn})`}</div>
          </div>
          <HeaderStatusBadges
            getStatusBadge={getStatusBadge}
            generalOrderDetails={props.orderData}
            authOnFileDetails={props.authOnFileDetails}
            allDenialRiskLabels={allDenialRiskLabels}
          />
        </div>
        <AuthorizationDetails
          insuranceName={props.insuranceName}
          dateOfService={props.dateOfService}
          providerName={props.providerName}
          location={props.location}
          primaryIcdCode={primaryIcdCodes}
          orderId={props.orderId}
          onEditIcdClick={() => {
            logPrimaryIcdEditButtonClicked({
              generalOrderDetails: props.orderData,
              coverageDetails: props.coverageDetails,
              authOnFile: props.authOnFileDetails,
              user,
              primaryIcdCodes,
              jcode_icd,
              submissionData: props.submissionData ?? ({} as Submission),
            });
            handleEditIcd(dispatch, props.orderId);
          }}
          onEditSiteNpiClick={() => handleEditSiteNpi(dispatch, props.orderId)}
          siteNpiDetails={
            props.submissionData?.test_location_submission_details
          }
          authOnFileDetails={props.authOnFileDetails}
        />
      </div>

      <HeaderActionButtons
        shouldShowNarRpaTrigger={shouldShowNarRpaTrigger}
        isNarRpaInProgress={isNarRpaInProgress}
        onNarRpaTriggerClick={handleNarRpaTriggerClick}
        shouldShowThreeDots={shouldShowThreeDots}
        guidelinesDocuments={guidelinesDocuments}
        healthCareFacilityData={{
          data: healthCareFacilityData,
          loading: isHealthCareFacilityLoading,
          error: healthCareFacilityError ?? null,
        }}
        onRefreshClick={handleAuthOnFileRefresh}
        isRefreshing={isRefreshing}
        hideButton={props.hideButton}
        authOnFileDetails={props.authOnFileDetails}
        generalOrderDetails={props.orderData}
        submissionData={props.submissionData ?? ({} as Submission)}
        primaryPayerName={primaryPayerName}
      />

      <HeaderModals
        openedModalId={openedModalId}
        orderId={props.orderId}
        generalOrderDetails={props.orderData}
        authOnFileDetails={props.authOnFileDetails}
        manualAuthStatus={manualAuthStatus}
        handleCloseModal={handleCloseModal}
        handleHappyFlowModalSave={handleHappyFlowModalSave}
        handleCommentRpaModalSave={handleCommentRpaModalSave}
        isLoading={isLoading}
        selectedProvider={selectedProvider}
      />
    </div>
  );
};

export default HeaderSingleOrder;
